import axios, { AxiosError } from "axios";

import { isSessionInvalidation401 } from "../constant/sessionConstants";
import { isSessionInterceptorRegistered } from "./axiosSessionInterceptor";

let interceptorId: number | null = null;

/**
 * Registers the global "plain 401 → logout redirect" Axios response
 * interceptor. Each app calls this once at its entry point (src/index.tsx,
 * next to setAppType) with its own unauthorized-redirect handler — replacing
 * the old import-time side effect in reactQuery/index.ts.
 *
 * Session-invalidation 401s (kicked by a new login / remote logout) are
 * SKIPPED here via the shared isSessionInvalidation401 predicate: the
 * session-kill interceptor owns those — cleanup + "Session Ended" overlay —
 * and if this handler claimed them first it would redirect to /login before
 * the overlay could show, leaving the user with no message at all. The skip
 * makes the messaging outcome independent of interceptor ordering.
 *
 * The skip is CONDITIONAL on that interceptor actually being registered.
 * It registers in SessionInterceptorProvider's mount effect while this one
 * registers at module scope in src/index.tsx, so before first render commit
 * (or in an app that never mounts the provider) an unconditional skip would
 * leave a session-kill 401 handled by nobody — React Query's global onError
 * only matches the literal message "Unauthorized", which these errors don't
 * carry. Falling back to the plain redirect is the lesser failure.
 *
 * Blob error bodies must be normalised BEFORE this runs, or the predicate
 * cannot see `data.code` at all — see axiosBlobErrorInterceptor.ts, which
 * every app entry registers first.
 *
 * Registration order still matters and this MUST run before the app renders:
 * axios executes response interceptors in registration order, and this
 * generic handler stays AHEAD of the session-kill interceptor. Re-calling it
 * at runtime is a no-op (below) precisely so it can never be re-appended
 * BEHIND that interceptor; tests wanting a clean slate call
 * removeUnauthorizedInterceptor() first.
 *
 * Consequence of that no-op worth knowing: a second call passing a DIFFERENT
 * `onUnauthorized` is silently ignored — the first callback stays bound. Each
 * app calls this exactly once at entry, so swapping the handler is not a
 * supported operation; call removeUnauthorizedInterceptor() first if you
 * genuinely need to rebind.
 */
export const setupUnauthorizedInterceptor = (onUnauthorized: () => void): void => {
  // Idempotent no-op, not eject+re-append: re-appending would reorder the
  // live chain (see above). Matters under vitest isolate:false too, where the
  // module registry — and axios — is shared across test files.
  if (interceptorId !== null) {
    return;
  }

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const isSessionKill = isSessionInterceptorRegistered() && isSessionInvalidation401(error);
      if (error.response?.status === 401 && !isSessionKill) {
        onUnauthorized();
      }
      return Promise.reject(error);
    },
  );
};

/**
 * Removes the unauthorized interceptor. Tests that register it explicitly
 * should call this in afterAll so it doesn't leak into later files sharing
 * the worker's module registry.
 */
export const removeUnauthorizedInterceptor = (): void => {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
  }
};
